/**
 * InsightsReview — V9 screen: "Here's what we found" (AC-3.4).
 *
 * Cards are bucketed by destination member (the survey Apply All writes to):
 * one section per member, own survey first — e.g. "Maylis Berron's Answers".
 * Each section contains, in order:
 *  1. "Double-check these" — that member's low-confidence matches (amber,
 *     collapsible). Each row shows group_label + identity caption.
 *  2. MULTI groups — one GroupSufficiencyCard per clustered group (e.g. employer)
 *  3. SINGLE groups — one GroupSufficiencyCard per remaining question group
 * And a page-level CTA — "Keep going — more to answer →" (opens
 * ApplyAllProgressModal), which writes all members' groups in one click.
 */
import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ChevronDownIcon from '@mui/icons-material/ExpandMore';
import ChevronUpIcon from '@mui/icons-material/ExpandLess';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAIFileUploadV2Context } from 'src/contexts/AIFileUploadV2Context';
import useAuth from 'src/hooks/useAuth';
import { useSummary } from 'src/hooks/aiFileUploadV2/useSummary';
import { useApplyAllProgress } from 'src/hooks/aiFileUploadV2/useApplyAllProgress';
import { patchMatch } from 'src/services/aiFileUploadV2';
import { deferFileUpload } from 'src/redux/slices/Interview/interviewSlice';
import type {
  ExtractionMatch,
  SummaryGroup,
  SummaryResponse,
} from 'src/types/aiFileUploadV2';
import { GroupSufficiencyCard } from './components/GroupSufficiencyCard';
import { ApplyAllProgressModal } from './components/ApplyAllProgressModal';
import { formatMatchValue } from './utils/formatMatchValue';

// ─── CollapsibleCard ──────────────────────────────────────────────────────────

interface CollapsibleCardProps {
  title: string;
  amber?: boolean;
  green?: boolean;
  defaultOpen?: boolean;
  /** When provided, overrides local open state (used by expand/collapse all). */
  forceOpen?: boolean;
  children: React.ReactNode;
}

function CollapsibleCard({
  title,
  amber = false,
  green = false,
  defaultOpen = true,
  forceOpen,
  children,
}: CollapsibleCardProps): JSX.Element {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  // Sync with parent's expand/collapse all when forceOpen changes.
  useEffect(() => {
    if (forceOpen !== undefined) {
      setOpen(forceOpen);
    }
  }, [forceOpen]);
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${
          amber
            ? theme.palette.warning.main
            : green
            ? theme.palette.success.main
            : theme.palette.divider
        }`,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 2px 12px rgba(0,0,0,0.3)'
            : '0 2px 12px rgba(26,25,24,0.03)',
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        onClick={() => setOpen((o) => !o)}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          borderBottom: open ? `1px solid ${theme.palette.divider}` : 'none',
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {open ? (
          <ChevronUpIcon
            sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
          />
        ) : (
          <ChevronDownIcon
            sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
          />
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

// ─── ProfileMatchBadge ─────────────────────────────────────────────────────────

/** Small pill shown next to a match's question name when the match targets a
 *  UserProfile field (DOB/SSN) rather than a questionnaire QIT — signals that
 *  confirming writes to the renter's profile, not an application question.
 *  Follows the same compact pill pattern as ConflictResolutionRow's
 *  "Multiple answers — pick one" badge. */
function ProfileMatchBadge(): JSX.Element {
  const theme = useTheme();
  return (
    <Box
      component="span"
      data-testid="profile-match-badge"
      sx={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        bgcolor: theme.palette.info.main + '1a',
        color: theme.palette.info.dark,
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span>Updates profile</span>
    </Box>
  );
}

// ─── ConfirmRow ───────────────────────────────────────────────────────────────

type ConfirmRowState = 'idle' | 'editing' | 'discarded';

interface ConfirmRowProps {
  match: ExtractionMatch;
  onPatch: (
    fileId: number,
    matchId: number,
    patch: { value?: string; user_confirmed?: boolean; user_excluded?: boolean }
  ) => Promise<ExtractionMatch | null>;
}

function ConfirmRow({ match, onPatch }: ConfirmRowProps): JSX.Element {
  const theme = useTheme();
  // Always start as "needs review" regardless of server state (user_confirmed=true
  // is just the default-accept default, not a real user action). Only flip to
  // confirmed after the user explicitly clicks "Looks right" or saves an edit.
  const [confirmed, setConfirmed] = useState(false);
  const [rowState, setRowState] = useState<ConfirmRowState>(
    match.user_excluded ? 'discarded' : 'idle'
  );
  const [editValue, setEditValue] = useState(
    match.user_edited_value ??
      match.proposed_answer_text ??
      match.extracted_value
  );
  const [busy, setBusy] = useState(false);

  const rawValue = confirmed
    ? editValue || match.proposed_answer_text
    : match.user_edited_value ??
      match.proposed_answer_text ??
      match.extracted_value;
  const displayValue = formatMatchValue(
    rawValue ?? '',
    match.value_type ?? match.qit?.question_type
  );

  const handleConfirm = useCallback(async () => {
    setBusy(true);
    const updated = await onPatch(match.renter_file_id, match.id, {
      user_confirmed: true,
    });
    setBusy(false);
    if (updated) {
      setConfirmed(true);
      setRowState('idle');
    }
  }, [match.id, match.renter_file_id, onPatch]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    const updated = await onPatch(match.renter_file_id, match.id, {
      value: editValue,
      user_confirmed: true,
    });
    setBusy(false);
    if (updated) {
      setConfirmed(true);
      setRowState('idle');
    }
  }, [match.id, match.renter_file_id, editValue, onPatch]);

  const handleDiscard = useCallback(async () => {
    setBusy(true);
    const updated = await onPatch(match.renter_file_id, match.id, {
      user_excluded: true,
    });
    setBusy(false);
    if (updated) {
      setConfirmed(false);
      setRowState('discarded');
    }
  }, [match.id, match.renter_file_id, onPatch]);

  const handleUndo = useCallback(async () => {
    setBusy(true);
    const updated = await onPatch(match.renter_file_id, match.id, {
      user_excluded: false,
      user_confirmed: true,
    });
    setBusy(false);
    if (updated) {
      setConfirmed(false); // return to unreviewed — user didn't say "Looks right"
      setRowState('idle');
    }
  }, [match.id, match.renter_file_id, onPatch]);

  const isDiscarded = rowState === 'discarded';

  // Group context header
  const groupHeader = match.group_label ? (
    <Box sx={{ mb: 0.75 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {match.group_label}
      </Typography>
      {match.proposed_group_identity_display && (
        <Typography sx={{ fontSize: 10, color: theme.palette.text.secondary }}>
          {match.proposed_group_identity_display}
        </Typography>
      )}
    </Box>
  ) : null;

  // ── Discarded state ──────────────────────────────────────────────────────────
  if (isDiscarded) {
    return (
      <Box
        sx={{
          borderRadius: '10px',
          backgroundColor: theme.palette.action.disabledBackground,
          px: 1.75,
          py: 1.25,
          mb: 1,
          opacity: 0.65,
        }}
      >
        {groupHeader}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <Typography
              sx={{ fontSize: 11, color: theme.palette.text.secondary }}
            >
              {match.question_name}
            </Typography>
            {match.profile_field != null && <ProfileMatchBadge />}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: theme.palette.text.disabled,
              textDecoration: 'line-through',
            }}
          >
            {displayValue}
          </Typography>
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={handleUndo}
            sx={{
              fontSize: 11,
              py: 0.25,
              px: 1,
              minWidth: 0,
              color: theme.palette.text.secondary,
            }}
          >
            {busy ? (
              <CircularProgress size={10} sx={{ color: 'inherit' }} />
            ) : (
              <span>Undo</span>
            )}
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Editing ──────────────────────────────────────────────────────────────────
  if (rowState === 'editing') {
    return (
      <Box
        sx={{
          borderRadius: '10px',
          backgroundColor: theme.palette.warning.main + '22',
          px: 1.75,
          py: 1.25,
          mb: 1,
        }}
      >
        {groupHeader}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
            mb: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{ fontSize: 11, color: theme.palette.text.secondary }}
          >
            {match.question_name}
          </Typography>
          {match.profile_field != null && <ProfileMatchBadge />}
        </Box>
        <input
          value={editValue ?? ''}
          onChange={(e) => setEditValue(e.target.value)}
          style={{
            width: '100%',
            fontSize: 15,
            fontWeight: 600,
            border: 'none',
            borderBottom: `2px solid ${theme.palette.primary.main}`,
            background: 'transparent',
            outline: 'none',
            padding: '2px 0',
            marginBottom: 8,
          }}
          autoFocus
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            disabled={busy || !editValue?.trim()}
            onClick={handleSave}
          >
            {busy ? (
              <CircularProgress size={12} sx={{ color: 'inherit' }} />
            ) : (
              <span>Save</span>
            )}
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={() => setRowState('idle')}
          >
            <span>Cancel</span>
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Idle / Confirmed ─────────────────────────────────────────────────────────
  if (confirmed) {
    // ── Confirmed: user explicitly approved this value ──────────────────────
    return (
      <Box
        sx={{
          borderRadius: '10px',
          border: `1px solid ${theme.palette.success.light}`,
          backgroundColor: theme.palette.success.main + '0f',
          px: 1.5,
          py: 1,
          mb: 1,
          position: 'relative',
        }}
      >
        {/* Check icon — top-right corner badge */}
        <CheckCircleIcon
          sx={{
            fontSize: 18,
            color: theme.palette.success.main,
            position: 'absolute',
            top: 10,
            right: 12,
          }}
        />
        {/* Content — no indent needed since icon is on the right */}
        <Box sx={{ pr: 3.5 }}>
          {groupHeader}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <Typography
              sx={{ fontSize: 11, color: theme.palette.text.secondary }}
            >
              {match.question_name}
            </Typography>
            {match.profile_field != null && <ProfileMatchBadge />}
          </Box>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: theme.palette.text.primary,
              overflowWrap: 'break-word',
            }}
          >
            {displayValue}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setConfirmed(false)}
            sx={{
              fontSize: 10,
              py: 0.2,
              px: 0.75,
              minWidth: 0,
              color: theme.palette.text.disabled,
            }}
          >
            <span>Undo</span>
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Unreviewed: user needs to take action ────────────────────────────────────
  return (
    <Box
      sx={{
        borderRadius: '10px',
        border: `1.5px solid ${theme.palette.warning.main}`,
        backgroundColor: theme.palette.warning.main + '12',
        px: 1.5,
        py: 1.25,
        mb: 1,
      }}
    >
      {groupHeader}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
          mb: 0.25,
          minWidth: 0,
        }}
      >
        <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
          {match.question_name}
        </Typography>
        {match.profile_field != null && <ProfileMatchBadge />}
      </Box>
      <Typography
        sx={{
          fontSize: 17,
          fontWeight: 700,
          color: theme.palette.text.primary,
          mb: 1,
          overflowWrap: 'break-word',
        }}
      >
        {displayValue}
      </Typography>
      {/* Action row — Remove first, Edit second, Looks right as primary CTA */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="text"
          disabled={busy}
          onClick={handleDiscard}
          sx={{
            fontSize: 12,
            py: 0.5,
            px: 1.5,
            flex: '1 1 auto',
            color: theme.palette.error.main,
            '&:hover': { backgroundColor: theme.palette.error.main + '14' },
          }}
        >
          {busy ? (
            <CircularProgress size={10} sx={{ color: 'inherit' }} />
          ) : (
            <span>Remove</span>
          )}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => setRowState('editing')}
          sx={{ fontSize: 12, py: 0.5, px: 1.5, flex: '1 1 auto' }}
        >
          <span>Edit</span>
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={busy}
          onClick={handleConfirm}
          sx={{ fontSize: 12, py: 0.5, px: 1.5, flex: '1 1 auto' }}
        >
          {busy ? (
            <CircularProgress size={10} sx={{ color: 'inherit' }} />
          ) : (
            <span>Looks right</span>
          )}
        </Button>
      </Box>
    </Box>
  );
}

// ─── Per-member destination buckets ───────────────────────────────────────────
//
// The summary response groups cards by content AND destination: every group and
// every low-confidence match carries `target_member_name` + `target_survey_id`
// (the member whose survey Apply All will write to). We re-bucket them by member
// so the review renders one section per destination, e.g.:
//
//   Maylis Berron's Answers     ← the uploader's own survey
//     [cards with her findings]
//   Tiffany Berron's Answers    ← a confirmed cross-member survey
//     [cards with her findings]

/** Key for the bucket whose target_member_name is null/undefined. */
const OWN_BUCKET_KEY = '__own__';

interface MemberBucket {
  /** Stable React key — the member name, or OWN_BUCKET_KEY when unnamed. */
  key: string;
  /** The household member's full name; null for the unnamed own-survey bucket. */
  name: string | null;
  /** True when this bucket is the uploader's own survey — detected by a null
   *  name or by `target_survey_id` matching the clicked survey. Rendered first. */
  isOwn: boolean;
  groups: SummaryGroup[];
  lowConfidenceMatches: ExtractionMatch[];
}

const bucketKeyFor = (name: string | null | undefined): string =>
  name ?? OWN_BUCKET_KEY;

/**
 * Bucket summary groups + low-confidence matches by destination member.
 *
 * The default partition's `target_member_name` is a real member name (the
 * clicked survey's member), so the own survey is detected via
 * `target_survey_id === ownSurveyId`, with a null name as a defensive fallback.
 * Deterministic order: own bucket first, then members in first-seen order
 * (Array.prototype.sort is stable, so equal-priority buckets keep insertion
 * order). Within a bucket, cards keep their server ordering.
 */
function buildMemberBuckets(
  summary: SummaryResponse,
  ownSurveyId: number | null
): MemberBucket[] {
  const bucketMap = new Map<string, MemberBucket>();
  const getOrCreate = (
    name: string | null | undefined,
    isOwnSurvey: boolean
  ): MemberBucket => {
    const key = bucketKeyFor(name);
    let bucket = bucketMap.get(key);
    if (!bucket) {
      bucket = {
        key,
        name: name ?? null,
        isOwn: name == null || isOwnSurvey,
        groups: [],
        lowConfidenceMatches: [],
      };
      bucketMap.set(key, bucket);
    } else if (isOwnSurvey) {
      bucket.isOwn = true;
    }
    return bucket;
  };

  for (const group of summary.groups) {
    const bucket = getOrCreate(
      group.target_member_name,
      group.target_survey_id === ownSurveyId
    );
    bucket.groups.push(group);
  }
  for (const match of summary.low_confidence_matches) {
    const bucket = getOrCreate(
      match.target_member_name,
      match.target_survey_id === ownSurveyId
    );
    bucket.lowConfidenceMatches.push(match);
  }

  return Array.from(bucketMap.values()).sort(
    (a, b) => Number(b.isOwn) - Number(a.isOwn)
  );
}

// ─── InsightsReview page ──────────────────────────────────────────────────────

interface InsightsReviewProps {
  /** When provided, completion calls this instead of navigating to /renter. */
  onComplete?: () => void;
  /** When provided, "upload more" calls this instead of navigating to uploadPath. */
  onUploadMore?: () => void;
}

const InsightsReview = ({
  onComplete,
  onUploadMore,
}: InsightsReviewProps = {}): JSX.Element | null => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // Derive the upload hub path — strip /review suffix
  const uploadPath = location.pathname.replace(/\/review$/, '');
  const theme = useTheme();
  const {
    surveyId,
    targetAnswerGroupId,
    targetQitId,
    registerWSHandlers,
    unregisterWSHandlers,
    loading: ctxLoading,
  } = useAIFileUploadV2Context();
  const { user } = useAuth();
  const { userId: routeUserId } = useParams<{ userId?: string }>();
  // Only treat :userId as proxy mode when the subject differs from the
  // logged-in user — cert-scoped routes include :userId for self-uploads too.
  const routeMemberId =
    routeUserId && user && Number(routeUserId) !== user.id
      ? Number(routeUserId)
      : undefined;
  const interviewFallbackUrl = routeMemberId
    ? `/renter/interview/${surveyId}/members/${routeMemberId}`
    : `/renter/interview/${surveyId}`;
  // ENG-2801: when the summary is already applied (410) inside the interview
  // embed, mirror handleContinueToInterview's exit — mark file_upload
  // session-deferred (deferFileUpload) so the IPE doesn't bounce the renter
  // back to the file-upload work item while pendingFileQits remain, then
  // advance via onComplete. Standalone (no onComplete) keeps useSummary's
  // navigate-back fallback.
  const handleAlreadyApplied = useCallback((): void => {
    dispatch(deferFileUpload());
    onComplete?.();
  }, [dispatch, onComplete]);
  const {
    summary,
    loading: summaryLoading,
    error,
    refetch,
  } = useSummary(
    surveyId,
    targetAnswerGroupId,
    onComplete ? handleAlreadyApplied : undefined,
    targetQitId
  );
  // Keep the spinner up while the context is still resolving the surveyId.
  // Without this, useSummary immediately sets loading=false when surveyId is
  // null, causing a brief "No insights" flash before the real fetch starts.
  const loading = ctxLoading || summaryLoading;

  // Apply All progress modal state
  const applyAll = useApplyAllProgress(
    surveyId,
    targetAnswerGroupId,
    targetQitId
  );
  const [modalOpen, setModalOpen] = useState(false);

  // Register / unregister WS handlers for Apply All events while this
  // screen is mounted. wsHandlers is stable (useMemo in useApplyAllProgress)
  // so this effect only re-runs when the context callbacks change.
  useEffect(() => {
    registerWSHandlers(applyAll.wsHandlers);
    return () => {
      unregisterWSHandlers(Object.keys(applyAll.wsHandlers));
    };
  }, [registerWSHandlers, unregisterWSHandlers, applyAll.wsHandlers]);

  // Expand / collapse all cards.
  // undefined = cards control themselves; true/false = forced by this button.
  const [forceOpen, setForceOpen] = useState<boolean | undefined>(undefined);
  const allExpanded = forceOpen !== false; // true or undefined → visually "expanded"

  const handleToggleAll = useCallback(() => {
    setForceOpen((prev) => (prev === false ? true : false));
  }, []);

  const handlePatch = useCallback(
    async (
      fileId: number,
      matchId: number,
      patch: {
        value?: string;
        user_confirmed?: boolean;
        user_excluded?: boolean;
      }
    ): Promise<ExtractionMatch | null> => {
      try {
        const result = await patchMatch(fileId, matchId, patch);
        // Debounced refetch — when ConflictResolutionRow fires Promise.all
        // with N patches simultaneously, each resolving patch calls this
        // handler. Without debouncing, N rapid refetch() calls fire in
        // quick succession: each doFetch() aborts the previous in-flight
        // summary request, and the axios interceptor transforms the
        // CanceledError into the string 'canceled' which useSummary's
        // catch block was previously misidentifying as a real error.
        //
        // The debounce collapses all N calls within a 50 ms window into a
        // single refetch, so only one summary round-trip fires regardless
        // of how many patches were batched.
        debouncedRefetchRef.current();
        return result;
      } catch {
        return null;
      }
    },
    // debouncedRefetchRef is a ref — stable identity, not a dep.
    []
  );

  // Track which low-confidence matches the user has explicitly handled
  // (confirmed or removed) in this session. Initialise from server state
  // for matches that are already excluded (user_excluded=true is the only
  // unambiguous "user acted" server signal; user_confirmed=true is also set
  // by default-accept so we cannot use it to seed the initial set).
  const [handledLowConfIds, setHandledLowConfIds] = useState<Set<number>>(
    () =>
      new Set(
        summary?.low_confidence_matches
          .filter((m) => m.user_excluded)
          .map((m) => m.id) ?? []
      )
  );

  const handleLowConfPatch = useCallback(
    async (
      fileId: number,
      matchId: number,
      patch: Parameters<typeof handlePatch>[2]
    ): Promise<ExtractionMatch | null> => {
      const result = await handlePatch(fileId, matchId, patch);
      if (result && (patch.user_confirmed || patch.user_excluded)) {
        setHandledLowConfIds((prev) => new Set([...prev, matchId]));
      }
      return result;
    },
    [handlePatch]
  );

  // Per-bucket "all handled" is computed inline in the render loop — each
  // member's "Double-check these" card turns green only when every one of that
  // member's low-confidence matches has been handled this session.

  // Debounced refetch: collapses rapid successive calls (e.g. from
  // ConflictResolutionRow's Promise.all) into a single summary reload.
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedRefetchRef = useRef(() => {
    if (refetchTimerRef.current) {
      clearTimeout(refetchTimerRef.current);
    }
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null;
      refetch();
    }, 50);
  });
  // Keep the closure fresh whenever refetch changes.
  useEffect(() => {
    debouncedRefetchRef.current = () => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
      }
      refetchTimerRef.current = setTimeout(() => {
        refetchTimerRef.current = null;
        refetch();
      }, 50);
    };
  }, [refetch]);

  const handleKeepGoing = useCallback(async () => {
    setModalOpen(true);
    const result = await applyAll.start();
    // Backend short-circuits when there are no insights to apply: no WS
    // events, no completion screen. Skip the modal entirely and continue
    // straight to the interview so the renter isn't left staring at an
    // empty backdrop.
    if (result.status === 'already_applied') {
      setModalOpen(false);
      dispatch(deferFileUpload());
      // ENG-2801: in the interview embed the screen is a portal (no route),
      // so advance via onComplete; route navigation would be a no-op and
      // leave the renter stuck on the same screen. Standalone navigates.
      if (onComplete) {
        onComplete();
        return;
      }
      const returnPathRef = (location.state as { returnPath?: string } | null)
        ?.returnPath;
      navigate(returnPathRef ?? interviewFallbackUrl);
    }
  }, [
    applyAll,
    dispatch,
    navigate,
    surveyId,
    location.state,
    onComplete,
    interviewFallbackUrl,
  ]);

  const handleUploadMore = useCallback(() => {
    if (onUploadMore) {
      onUploadMore();
      return;
    }
    navigate(uploadPath);
  }, [navigate, uploadPath, onUploadMore]);

  // CTAs from the modal's completed state.
  const handleContinueToInterview = useCallback(() => {
    // DVT and AI file upload are both done at this point — navigate directly
    // to the interview without any redirect checks.
    //
    // Mark the file_upload work item as session-deferred so the IPE doesn't
    // bounce the renter right back to /renter/files when Apply All only
    // covered some of the visible file QITs (partial-Apply-All exit). On
    // refresh, the deferred flag clears and IPE re-surfaces file_upload —
    // correct behaviour, the renter has had time away.
    const returnPath =
      (location.state as { returnPath?: string } | null)?.returnPath ?? null;
    dispatch(deferFileUpload());
    if (onComplete) {
      onComplete();
      return;
    }
    navigate(returnPath ?? interviewFallbackUrl);
  }, [
    navigate,
    location.state,
    surveyId,
    dispatch,
    onComplete,
    interviewFallbackUrl,
  ]);

  const handleGoHome = useCallback(() => {
    // Strip the /files/... segment to navigate back to the certification page.
    const certPath = uploadPath.replace(/\/files.*$/, '');
    navigate(certPath || '/renter');
  }, [navigate, uploadPath]);

  const handleRetry = useCallback(() => {
    applyAll.reset();
    setModalOpen(false);
  }, [applyAll]);

  const handleCloseModal = useCallback(() => {
    applyAll.reset();
    setModalOpen(false);
  }, [applyAll]);

  if (loading) {
    return (
      <Box
        sx={{
          maxWidth: 480,
          mx: 'auto',
          px: 2.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100%',
          backgroundColor: theme.palette.background.default,
          gap: 2,
        }}
      >
        <CircularProgress size={36} />
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: theme.palette.text.primary,
              letterSpacing: -0.3,
            }}
          >
            Pulling together your answers…
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: theme.palette.text.secondary,
              mt: 0.75,
              lineHeight: 1.5,
            }}
          >
            This usually takes just a moment.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 2, py: 4, maxWidth: 480, mx: 'auto' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const hasInsights =
    !!summary &&
    (summary.low_confidence_matches.length > 0 || summary.groups.length > 0);
  // One section per destination member (own survey first, then members in
  // first-seen order). Each bucket keeps its cards in server order.
  const memberBuckets = summary ? buildMemberBuckets(summary, surveyId) : [];

  // Detect unresolved confidence ties in group-root answers.
  //
  // A conflict bucket is a set of group_answers for the same qit_id within
  // one group where size > 1 (backend ships all tied candidates). It is
  // "unresolved" if none of the candidates has been picked yet — i.e. none
  // satisfy user_confirmed=true && user_excluded=false simultaneously while
  // at least one other candidate is still active (not excluded).
  //
  // When any unresolved conflict exists, the "Keep going" CTA is disabled and
  // an inline banner points the user to the highlighted rows.
  const hasUnresolvedConflicts = (summary?.groups ?? []).some((group) => {
    const visibleAnswers = group.group_answers.filter((m) => !m.is_file_match);
    // Bucket by qit_id
    const byQit = new Map<number, typeof visibleAnswers>();
    for (const m of visibleAnswers) {
      const qitId = m.qit?.id;
      if (qitId == null) {
        continue;
      }
      const bucket = byQit.get(qitId) ?? [];
      bucket.push(m);
      byQit.set(qitId, bucket);
    }
    // A bucket with >1 entry = conflict shipped by backend.
    // Unresolved = no candidate is confirmed + not excluded.
    for (const bucket of byQit.values()) {
      if (bucket.length <= 1) {
        continue;
      }
      // Only a real conflict when the tied candidates have distinct values.
      // Strip whitespace; preserve case (no lowercasing).
      const distinctValues = new Set(
        bucket.map((m) =>
          (m.proposed_answer_text ?? m.extracted_value ?? '').trim()
        )
      );
      if (distinctValues.size <= 1) {
        continue;
      }
      const hasWinner = bucket.some(
        (m) => m.user_confirmed && !m.user_excluded
      );
      if (!hasWinner) {
        return true;
      }
    }
    return false;
  });

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: 'auto',
        px: 2.5,
        pt: 3,
        pb: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: theme.palette.background.default,
        minHeight: '100%',
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          fontSize: 26,
          fontWeight: 600,
          color: theme.palette.text.primary,
          letterSpacing: -0.5,
        }}
      >
        Here&apos;s what we found
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          fontSize: 14,
          color: theme.palette.text.secondary,
          lineHeight: 1.5,
        }}
      >
        We filled in what we could from your documents. Confirm the details
        below, then we&apos;ll go through the remaining questions together.
      </Typography>

      {/* Expand / collapse all — only shown when there is content */}
      {hasInsights && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            size="small"
            variant="text"
            onClick={handleToggleAll}
            startIcon={
              allExpanded ? (
                <UnfoldLessIcon fontSize="small" />
              ) : (
                <UnfoldMoreIcon fontSize="small" />
              )
            }
            sx={{
              fontSize: 12,
              color: theme.palette.text.secondary,
              textTransform: 'none',
            }}
          >
            <span>{allExpanded ? 'Collapse all' : 'Expand all'}</span>
          </Button>
        </Box>
      )}

      {!hasInsights ? (
        <Typography
          sx={{
            textAlign: 'center',
            color: theme.palette.text.secondary,
            fontSize: 15,
            py: 6,
          }}
        >
          No insights to review yet — keep uploading.
        </Typography>
      ) : (
        <>
          {/* One section per destination member. Own survey first, then
              confirmed members in first-seen order. */}
          {memberBuckets.map((bucket) => {
            const multiGroups = bucket.groups.filter((g) => g.kind === 'multi');
            const singleGroups = bucket.groups.filter(
              (g) => g.kind === 'single'
            );
            const bucketAllLowConfHandled =
              bucket.lowConfidenceMatches.length > 0 &&
              bucket.lowConfidenceMatches.every((m) =>
                handledLowConfIds.has(m.id)
              );
            return (
              <Box
                key={bucket.key}
                data-testid="member-section"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {/* Member heading, e.g. "Maylis Berron's Answers" */}
                <Typography
                  data-testid="member-heading"
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    letterSpacing: -0.2,
                  }}
                >
                  {bucket.name ? `${bucket.name}'s Answers` : 'Your answers'}
                </Typography>

                {/* Low-confidence matches for this member */}
                {bucket.lowConfidenceMatches.length > 0 && (
                  <CollapsibleCard
                    title="Double-check these"
                    amber={!bucketAllLowConfHandled}
                    green={bucketAllLowConfHandled}
                    forceOpen={forceOpen}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: theme.palette.text.secondary,
                        lineHeight: 1.5,
                        mb: 1.5,
                      }}
                    >
                      We weren&apos;t fully confident about these values.
                      Confirm the ones that look right, edit any that are wrong,
                      or remove ones we got wrong entirely.
                    </Typography>
                    {bucket.lowConfidenceMatches.map((match) => (
                      <ConfirmRow
                        key={match.id}
                        match={match}
                        onPatch={handleLowConfPatch}
                      />
                    ))}
                  </CollapsibleCard>
                )}

                {/* MULTI groups (e.g. pay stubs per employer) */}
                {multiGroups.map((group) => (
                  <GroupSufficiencyCard
                    key={group.group_key}
                    group={group}
                    forceOpen={forceOpen}
                    onPatchMatch={handlePatch}
                    onUploadMore={handleUploadMore}
                  />
                ))}

                {/* SINGLE groups (e.g. household info, one-off answers) */}
                {singleGroups.map((group) => (
                  <GroupSufficiencyCard
                    key={group.group_key}
                    group={group}
                    forceOpen={forceOpen}
                    onPatchMatch={handlePatch}
                    onUploadMore={handleUploadMore}
                  />
                ))}
              </Box>
            );
          })}
        </>
      )}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Conflict blocker banner — shown above the CTA when there are
          unresolved tied matches. Points the user to the highlighted rows. */}
      {hasUnresolvedConflicts && (
        <Box
          sx={{
            bgcolor: theme.palette.warning.main + '22',
            border: `1px solid ${theme.palette.warning.main}`,
            borderRadius: 1.5,
            px: 2,
            py: 1.25,
          }}
          data-testid="conflict-banner"
        >
          <Typography
            sx={{
              fontSize: 13,
              color: theme.palette.warning.dark,
              lineHeight: 1.5,
            }}
          >
            Some questions have multiple equally-confident answers. Pick one
            answer in each highlighted section to continue.
          </Typography>
        </Box>
      )}

      {/* CTA */}
      <Button
        variant="contained"
        fullWidth
        disabled={applyAll.phase !== 'idle' || hasUnresolvedConflicts}
        onClick={handleKeepGoing}
        endIcon={<ArrowForwardIcon />}
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontSize: 16,
          fontWeight: 600,
          height: 56,
          borderRadius: 2,
          '&:hover': { backgroundColor: theme.palette.primary.dark },
        }}
        disableElevation
        data-testid="keep-going-btn"
      >
        <span>Continue</span>
      </Button>

      {/* Apply All progress modal */}
      <ApplyAllProgressModal
        open={modalOpen}
        phase={applyAll.phase}
        steps={applyAll.steps}
        currentStepIndex={applyAll.currentStepIndex}
        error={applyAll.error}
        onContinue={handleContinueToInterview}
        onRetry={handleRetry}
        onClose={handleCloseModal}
      />
    </Box>
  );
};

export default InsightsReview;
